import { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import axios from "axios";
import { LogOut, Mail, Phone, Building2, Clapperboard, ArrowLeft, Download, CheckCircle2, RotateCcw, Trash2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-[#050505] text-neutral-100 antialiased">
      <div className="grain-overlay" aria-hidden="true" />
      <div className="mx-auto max-w-5xl px-5 py-16 md:px-10">{children}</div>
    </div>
  );
}

function SignIn() {
  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <Shell>
      <div className="flex min-h-[70vh] flex-col items-center justify-center text-center">
        <img
          src="/assets/veritas-seal-192.png"
          alt="Veritas Advisory LLP seal"
          className="h-20 w-20 rounded-full border border-[#B79045]/40"
        />
        <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.3em] text-[#D4AF37]">
          Veritas Advisory LLP
        </p>
        <h1 className="mt-3 font-display text-3xl font-medium sm:text-4xl">
          Enquiries <em className="italic text-[#D4AF37]">Dashboard</em>
        </h1>
        <p className="mt-4 max-w-sm text-sm leading-relaxed text-neutral-400">
          Private area. Sign in with your Google account to view website enquiries.
        </p>
        <button
          data-testid="admin-google-login-button"
          onClick={handleLogin}
          className="mt-10 border border-[#B79045]/60 px-8 py-4 font-mono text-[11px] uppercase tracking-[0.2em] text-[#D4AF37] transition-colors duration-300 hover:bg-[#B79045] hover:text-black"
        >
          Sign in with Google
        </button>
        <Link
          data-testid="admin-back-home-link"
          to="/"
          className="mt-8 flex items-center gap-2 text-xs text-neutral-500 transition-colors duration-300 hover:text-[#D4AF37]"
        >
          <ArrowLeft size={13} /> Back to website
        </Link>
      </div>
    </Shell>
  );
}

function Denied({ user, onLogout }) {
  return (
    <Shell>
      <div className="flex min-h-[70vh] flex-col items-center justify-center text-center">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#D4AF37]">Access restricted</p>
        <h1 data-testid="admin-access-denied" className="mt-3 font-display text-3xl font-medium">
          This account is not authorised
        </h1>
        <p className="mt-4 max-w-md text-sm text-neutral-400">
          {user.email} does not have access to the Veritas enquiries dashboard.
        </p>
        <button
          data-testid="admin-denied-logout-button"
          onClick={onLogout}
          className="mt-10 border border-white/20 px-6 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-300 transition-colors duration-300 hover:border-[#D4AF37] hover:text-[#D4AF37]"
        >
          Sign out
        </button>
      </div>
    </Shell>
  );
}

function EnquiryCard({ enq, onStatus, onDelete }) {
  const date = new Date(enq.created_at).toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
  const replied = enq.status === "replied";
  return (
    <article
      data-testid={`enquiry-card-${enq.id}`}
      className="border border-white/10 bg-[#0a0a0a] p-6 transition-colors duration-300 hover:border-[#B79045]/40"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h3 className="font-display text-xl text-neutral-100">{enq.name}</h3>
          <span
            data-testid={`enquiry-status-${enq.id}`}
            className={`border px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.2em] ${
              replied ? "border-[#B79045]/60 text-[#D4AF37]" : "border-white/20 text-neutral-400"
            }`}
          >
            {replied ? "Replied" : "New"}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">{date}</span>
          <button
            data-testid={`enquiry-toggle-status-${enq.id}`}
            onClick={() => onStatus(enq.id, replied ? "new" : "replied")}
            title={replied ? "Mark as new" : "Mark as replied"}
            className="flex h-8 w-8 items-center justify-center border border-white/15 text-neutral-400 transition-colors duration-300 hover:border-[#D4AF37] hover:text-[#D4AF37]"
          >
            {replied ? <RotateCcw size={13} /> : <CheckCircle2 size={13} />}
          </button>
          <button
            data-testid={`enquiry-delete-${enq.id}`}
            onClick={() => onDelete(enq.id)}
            title="Delete enquiry"
            className="flex h-8 w-8 items-center justify-center border border-white/15 text-neutral-400 transition-colors duration-300 hover:border-red-500/70 hover:text-red-400"
          >
            <Trash2 size={13} />
          </button>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm text-neutral-400">
        <a href={`mailto:${enq.email}`} className="flex items-center gap-2 hover:text-[#D4AF37]">
          <Mail size={13} className="text-[#B79045]" /> {enq.email}
        </a>
        {enq.phone && (
          <a href={`tel:${enq.phone}`} className="flex items-center gap-2 hover:text-[#D4AF37]">
            <Phone size={13} className="text-[#B79045]" /> {enq.phone}
          </a>
        )}
        {enq.company && (
          <span className="flex items-center gap-2">
            <Building2 size={13} className="text-[#B79045]" /> {enq.company}
          </span>
        )}
        {enq.production_type && (
          <span className="flex items-center gap-2">
            <Clapperboard size={13} className="text-[#B79045]" /> {enq.production_type}
          </span>
        )}
      </div>
      <p className="mt-4 whitespace-pre-line border-t border-white/5 pt-4 text-sm leading-relaxed text-neutral-300">
        {enq.message}
      </p>
    </article>
  );
}

export default function Admin() {
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [checking, setChecking] = useState(!location.state?.user);
  const [enquiries, setEnquiries] = useState(null);

  useEffect(() => {
    if (user) return;
    axios
      .get(`${API}/api/auth/me`, { withCredentials: true })
      .then((res) => setUser(res.data))
      .catch(() => setUser(null))
      .finally(() => setChecking(false));
  }, [user]);

  useEffect(() => {
    if (!user?.is_admin) return;
    axios
      .get(`${API}/api/enquiries`, { withCredentials: true })
      .then((res) => setEnquiries(res.data))
      .catch(() => setEnquiries([]));
  }, [user]);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/api/auth/logout`, {}, { withCredentials: true });
    } finally {
      setUser(null);
      setEnquiries(null);
    }
  };

  const handleStatus = async (id, status) => {
    await axios.patch(`${API}/api/enquiries/${id}`, { status }, { withCredentials: true });
    setEnquiries((prev) => prev.map((e) => (e.id === id ? { ...e, status } : e)));
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this enquiry permanently?")) return;
    await axios.delete(`${API}/api/enquiries/${id}`, { withCredentials: true });
    setEnquiries((prev) => prev.filter((e) => e.id !== id));
  };

  const handleExport = async () => {
    const res = await axios.get(`${API}/api/enquiries/export`, {
      withCredentials: true,
      responseType: "blob",
    });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = "veritas-enquiries.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  if (checking) {
    return (
      <Shell>
        <div data-testid="admin-loading" className="flex min-h-[70vh] items-center justify-center">
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#D4AF37]">Loading…</p>
        </div>
      </Shell>
    );
  }

  if (!user) return <SignIn />;
  if (!user.is_admin) return <Denied user={user} onLogout={handleLogout} />;

  return (
    <Shell>
      <header className="flex flex-wrap items-end justify-between gap-6 border-b border-white/10 pb-8">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#D4AF37]">
            Veritas Advisory LLP
          </p>
          <h1 data-testid="admin-dashboard-title" className="mt-2 font-display text-4xl font-medium sm:text-5xl">
            Enquiries <em className="italic text-[#D4AF37]">Dashboard</em>
          </h1>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p data-testid="admin-user-name" className="text-sm text-neutral-200">{user.name}</p>
            <p className="text-xs text-neutral-500">{user.email}</p>
          </div>
          {user.picture && (
            <img src={user.picture} alt="" className="h-10 w-10 rounded-full border border-[#B79045]/40" />
          )}
          <button
            data-testid="admin-logout-button"
            onClick={handleLogout}
            title="Sign out"
            className="flex h-10 w-10 items-center justify-center border border-white/15 text-neutral-400 transition-colors duration-300 hover:border-[#D4AF37] hover:text-[#D4AF37]"
          >
            <LogOut size={15} />
          </button>
        </div>
      </header>

      <div className="mt-6 flex items-center justify-between">
        <p data-testid="admin-enquiry-count" className="font-mono text-[11px] uppercase tracking-[0.25em] text-neutral-500">
          {enquiries === null ? "Loading enquiries…" : `${enquiries.length} enquiries`}
        </p>
        <div className="flex items-center gap-5">
          <button
            data-testid="admin-export-csv-button"
            onClick={handleExport}
            disabled={!enquiries?.length}
            className="flex items-center gap-2 border border-[#B79045]/60 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] transition-colors duration-300 hover:bg-[#B79045] hover:text-black disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Download size={13} /> Export CSV
          </button>
          <Link
            data-testid="admin-view-site-link"
            to="/"
            className="flex items-center gap-2 text-xs text-neutral-500 transition-colors duration-300 hover:text-[#D4AF37]"
          >
            <ArrowLeft size={13} /> View website
          </Link>
        </div>
      </div>

      <div data-testid="admin-enquiries-list" className="mt-6 space-y-5">
        {enquiries?.length === 0 && (
          <p data-testid="admin-no-enquiries" className="py-16 text-center text-sm text-neutral-500">
            No enquiries yet.
          </p>
        )}
        {enquiries?.map((enq) => (
          <EnquiryCard key={enq.id} enq={enq} onStatus={handleStatus} onDelete={handleDelete} />
        ))}
      </div>
    </Shell>
  );
}
